import type {ProCoverageItem, ProDetailsData} from '../types';

import {dateToReadableDate} from '@date-picker';
import {Button, Loading} from '@ds/components';
import {DetailPageLayout} from '@ds/layout';
import {toast} from '@Toast';
import {useEffect, useMemo, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from 'store';
import {
  deleteVendorPro,
  getVendorProDetail,
  markVendorProDone
} from 'store/slices/vendor-pro/thunk';

import ProAreaSelect from '../components/common/ProAreaSelect/ProAreaSelect';
import ProCoverageMap from '../components/common/ProCoverageMap/ProCoverageMap';
import ProInfoRow from '../components/common/ProInfoRow/ProInfoRow';
import ProStatus from '../components/common/ProStatus/ProStatus';
import {PRO_ROUTES} from '../constants';
import {buildCoverageMapPolygons, getCoveragePolygonIds, getProVendorId} from '../utils';
import ProActionModal from './components/ProActionModal/ProActionModal';

const ProDetails: React.FC = () => {
  const vendorId = getProVendorId();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {vendorProId} = useParams();
  const {details, detailLoading, remove, done} = useAppSelector((state) => state.vendorPro);
  const campaign = details as ProDetailsData | null;

  useEffect(() => {
    void dispatch(
      getVendorProDetail({
        vendor_id: vendorId as number,
        vendor_pro_id: vendorProId as unknown as number
      })
    );
  }, [dispatch, vendorId, vendorProId]);

  const pauseCampaign = () => {
    if (!campaign?.id) {
      return;
    }

    void dispatch(markVendorProDone({vendor_pro_id: campaign.id, vendor_id: vendorId as number}))
      .then(() => {
        setIsConfirmOpen(false);
        navigate(PRO_ROUTES.list);
      })
      .catch((error: {data?: {errors?: {message?: string}}}) => {
        toast(error.data?.errors?.message || 'مشکلی پیش آمده. دوباره تلاش کنید!');
      });
  };

  const deleteCampaign = () => {
    if (!campaign?.id) {
      return;
    }

    void dispatch(deleteVendorPro({vendor_pro_id: campaign.id, vendor_id: vendorId as number}))
      .then(() => {
        setIsConfirmOpen(false);
        navigate(PRO_ROUTES.list);
      })
      .catch((error: {data?: {errors?: {message?: string}}}) => {
        toast(error.data?.errors?.message || 'مشکلی پیش آمده. دوباره تلاش کنید!');
      });
  };

  const coverageItems = campaign?.items as ProCoverageItem[] | undefined;
  const selectedPolygonIds = useMemo(() => getCoveragePolygonIds(coverageItems), [coverageItems]);
  const polygons = useMemo(
    () => buildCoverageMapPolygons(coverageItems, selectedPolygonIds),
    [coverageItems, selectedPolygonIds]
  );
  const latitude = campaign?.latitude ?? 35.6892;
  const longitude = campaign?.longitude ?? 51.389;
  const canAct = campaign?.state === 'INIT' || campaign?.state === 'ACTIVE';

  if (detailLoading) {
    return (
      <div className="flex min-h-60 flex-1 items-center justify-center">
        <Loading size="large" />
      </div>
    );
  }

  return (
    <>
      <DetailPageLayout
        contentClassName="px-4"
        footer={
          canAct ? (
            <Button
              fullWidth
              size="lg"
              variant="fill"
              color="destructive"
              onClick={() => setIsConfirmOpen(true)}
            >
              {campaign?.state === 'INIT' ? 'حذف' : 'توقف'}
            </Button>
          ) : undefined
        }
      >
        <ProInfoRow title="وضعیت" value={<ProStatus value={campaign?.state} />} />
        <ProInfoRow
          title="تاریخ شروع"
          value={dateToReadableDate({
            date: campaign?.start_date ?? '',
            day: true,
            month: true,
            year: true
          })}
        />
        {campaign?.state === 'DONE' ? (
          <ProInfoRow
            title="تاریخ پایان"
            value={dateToReadableDate({
              date: campaign.end_date ?? '',
              day: true,
              month: true,
              year: true
            })}
          />
        ) : null}
        <ProCoverageMap
          latitude={latitude}
          longitude={longitude}
          polygons={polygons}
          selectedPolygonIds={selectedPolygonIds}
        />
        <section className="mt-2">
          <h3 className="mt-6 mb-0 text-subtitle-md text-neutral-fg">منطقه تحت پوشش</h3>
          <ProAreaSelect readOnly showMap={false} showHeader={false} coverageItems={coverageItems} />
        </section>
      </DetailPageLayout>
      <ProActionModal
        isOpen={isConfirmOpen}
        loading={Boolean(remove.loading || done.loading)}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={campaign?.state === 'INIT' ? deleteCampaign : pauseCampaign}
      />
    </>
  );
};

export default ProDetails;
